import React from 'react';
import { MobileScheduleCalendar, ProviderCancelSessionModal, SessionDetailsModal } from '@uplift/ui-v2';
import { useCalendarStore, useCurrentuser, useTimezone } from '@uplift/contexts';
import { observer } from 'mobx-react-lite';
import { useQueryClient } from '@tanstack/react-query';
import { DateFormats } from '@uplift/constants';
import { DisplayName } from '@uplift/utils';
import { UserStatus } from '@uplift/types';
import { Appointment } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { AddendumCorrectionSections } from '../../../../../components/addendum-corrections';

export const CalendarMobile: React.FC = observer(() => {
  const queryClient = useQueryClient();
  const [ openedModal, setOpenedModal ] = React.useState<'details' | 'cancel'>();
  const [ selectedEvent, setSelectedEvent ] = React.useState<Appointment>();
  const [ patientDisplayName, setPatientDisplayName ] = React.useState<DisplayName>({ first: '', last: '' });
  const [ patientStatus, setPatientStatus ] = React.useState<UserStatus>();
  const me = useCurrentuser();
  const timezone = useTimezone();
  const calendarStore = useCalendarStore();

  return (
    <>
      <MobileScheduleCalendar
        onEventClick={(event) => {
          setSelectedEvent(event);
          setOpenedModal('details');
        }}
        getAvailableBookingSlotRange={async (date, availableSlots) => {
          calendarStore.allowedSlots = availableSlots;
          return calendarStore.getFirstAvailableCreateAppointmentRange({
            start: date,
            timezone: timezone,
            medicalRole: me.medicalRole,
          });
        }}
      />
      <SessionDetailsModal
        open={openedModal === 'details'}
        onClose={() => setOpenedModal(undefined)}
        onCancelSession={(_id, name, status) => {
          setPatientDisplayName(name);
          setPatientStatus(status);
          setOpenedModal('cancel');
        }}
        appointment={{ ...selectedEvent, parentId: selectedEvent?.appointmentSeriesId }}
        keepMounted
        correctionSections={(ctx) => <AddendumCorrectionSections {...ctx} />}
      />
      <ProviderCancelSessionModal
        open={openedModal === 'cancel'}
        appointmentId={selectedEvent?.id}
        parentId={selectedEvent?.appointmentSeriesId}
        fromDatetime={selectedEvent?.fromDatetime}
        onSubmit={async () => {
          const date = CalendarDateService.date(selectedEvent.fromDatetime).format(DateFormats.ISO_DATE);

          await queryClient.invalidateQueries({ queryKey: [ 'calendar', date ] });
          await queryClient.invalidateQueries({ queryKey: [ 'calendar', 'booked-days' ] });

          setPatientDisplayName(undefined);
          setPatientStatus(undefined);
        }}
        onClose={() => {
          setOpenedModal(undefined);
        }}
        patientDisplayName={patientDisplayName}
        patientId={selectedEvent?.appointmentSeriesId}
        patientStatus={patientStatus}
      />
    </>
  );
});
